// Free Pro for nonprofits (OpenSpec 136). The wording of the Settings card, the admin page, the
// public page and the pricing FAQ lives here, so tests check one place. Claims rule: state only
// what the server does. Pro limits come from `plans.ts`, the term is 12 months, and the sources
// named are the ones the server imports.
import type {
  NonprofitApplication,
  NonprofitApplicationInput,
  NonprofitGrant,
  NonprofitGrantSource,
  NonprofitVerdict,
} from '@/lib/api-v2-client'
import type { StatusKind } from '@/components/status-badge'

/** Months of one grant. The renewal sweep adds the same again while the domain still qualifies. */
export const GRANT_MONTHS = 12

export const NONPROFIT_PAGE = '/for/nonprofits'

/** The terms paragraph. The pricing FAQ entry (`NONPROFIT_FAQ`) says the same. */
export const NONPROFIT_TERMS = `Registered nonprofits and charities can get the Pro plan free for ${GRANT_MONTHS} months. We can end a grant if the organization no longer qualifies or if the information given was not true. We can also end a grant for a government body, a political party, a group whose mission is religious worship or a for-profit social enterprise. When a grant ends, the organization moves to the plan of its subscription, or to the Free plan if it has none.`

/** A date as the billing tab shows it: "October 24, 2027". */
export function grantDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', timeZone: 'UTC' })
}

/** The Current Plan line for an organization with a live grant. */
export function sponsoredUntil(endsAt: string): string {
  return `Sponsored for nonprofits until ${grantDate(endsAt)}.`
}

/** Grant-only organizations (no live subscription) can still buy Team (D13). */
export const TEAM_OVER_GRANT =
  'Team runs on top of the nonprofit grant. If you cancel Team later, you go back to Pro while the grant lasts.'

/** The badge of an application or grant status, the same on the owner card and the admin page. */
export const STATUS_BADGES: Record<NonprofitApplication['status'] | NonprofitGrant['status'], { kind: StatusKind; label: string }> = {
  pending: { kind: 'pending', label: 'Pending' },
  approved: { kind: 'up', label: 'Approved' },
  rejected: { kind: 'down', label: 'Rejected' },
  active: { kind: 'up', label: 'Active' },
  ended: { kind: 'unknown', label: 'Ended' },
  revoked: { kind: 'down', label: 'Revoked' },
}

/** Where a grant or a listed domain came from, as a phrase after "Listed in" or "through". */
export const SOURCE_LABELS: Record<NonprofitGrantSource, string> = {
  tld: 'the .ngo or .ong domain',
  curated: 'our list of nonprofits',
  uk_charity_commission: 'the UK Charity Commission register',
  acnc: 'the Australian ACNC register',
  manual: 'an approved application',
  admin: 'a grant from Uptrack',
}

export function sourceLabel(source: string | null | undefined): string {
  return (source && SOURCE_LABELS[source as NonprofitGrantSource]) || 'our list of nonprofits'
}

/** The badge and the plain-language reason for each domain verdict. Never color alone. */
export const VERDICTS: Record<NonprofitVerdict, { kind: StatusKind; label: string; reason: string }> = {
  eligible: { kind: 'up', label: 'Qualifies', reason: 'This domain qualifies for free Pro.' },
  blocked: {
    kind: 'down',
    label: 'Blocked',
    reason: 'This domain cannot get free Pro. If you think this is wrong, email support@uptrack.app.',
  },
  government: { kind: 'unknown', label: 'Not eligible', reason: 'Government domains do not qualify.' },
  taken: {
    kind: 'degraded',
    label: 'In use',
    reason: 'Another Uptrack organization already has free Pro for this domain.',
  },
  not_listed: {
    kind: 'unknown',
    label: 'Not listed',
    reason: 'This domain is not on our lists. You can apply with your registry number.',
  },
}

/** The reason line of one verdict; an eligible domain names its source. */
export function verdictReason(verdict: NonprofitVerdict, source: string | null): string {
  if (verdict === 'eligible') return `${VERDICTS.eligible.reason} Listed in ${sourceLabel(source)}.`
  return VERDICTS[verdict]?.reason ?? ''
}

export const NONPROFIT_COPY = {
  title: 'Nonprofit program',
  intro: `Registered nonprofits and charities get Pro free for ${GRANT_MONTHS} months. It renews while you still qualify.`,
  noDomains:
    "No domain to check yet. Free Pro needs a member with a confirmed email address on your organization's domain. Add a work email address in Settings, Profile.",
  membersOnly: 'Only an owner or admin of the organization can claim free Pro or apply.',
  claim: 'Claim free Pro',
  claimed: `Free Pro is on for ${GRANT_MONTHS} months.`,
  cancelPaid:
    'You also pay for a Pro subscription. You can cancel it where you subscribed. Pro continues through the nonprofit grant. The paid plan stays until the end of its billing period.',
  pendingTitle: 'Application in review',
  pending: 'We check your registry number against the public register and email you our decision.',
  rejectedTitle: 'Application not approved',
  applyTitle: 'Apply for free Pro',
  applyIntro:
    'Your domain is not on our lists. Tell us where your organization is registered. We check it against the public register.',
  applySubmit: 'Send application',
  applySent: 'Application sent. We email you our decision.',
  renews: 'It renews each year while your domain still qualifies and a member still has a confirmed email address on it.',
} as const

/** The grant line in the Nonprofit card. */
export function grantSummary(grant: { status: string; domain: string; source: string; ends_at: string }): string {
  if (grant.status === 'active') {
    return `Pro is free for ${grant.domain} until ${grantDate(grant.ends_at)}, through ${sourceLabel(grant.source)}.`
  }
  if (grant.status === 'revoked') return `Free Pro for ${grant.domain} was ended by Uptrack.`
  return `Free Pro for ${grant.domain} ended on ${grantDate(grant.ends_at)}.`
}

/** Error codes of the owner endpoints, in plain words. The server message is the fallback. */
export const NONPROFIT_ERRORS: Record<string, string> = {
  nonprofit_program_disabled: 'The nonprofit program is not open right now.',
  owner_required: 'Only an owner or admin of the organization can do this.',
  already_granted: 'Your organization already has free Pro.',
  not_eligible: 'This domain does not qualify. Check the reason next to it.',
  domain_taken: 'Another organization just claimed free Pro for this domain.',
  invalid_application: 'Some fields are not valid. Check them and try again.',
  application_pending: 'You already have an application in review.',
  too_many_applications: 'You sent 5 applications in the last 30 days. Try again later.',
  domain_not_allowed: 'This domain cannot be used for an application. Check the reason next to it.',
}

export function nonprofitErrorMessage(err: unknown): string {
  const e = err as { code?: unknown; message?: unknown } | null
  if (e && typeof e.code === 'string' && NONPROFIT_ERRORS[e.code]) return NONPROFIT_ERRORS[e.code]
  if (e && typeof e.message === 'string' && e.message) return e.message
  return 'Something went wrong. Try again.'
}

/** Field limits of the server (design section 2 CHECKs and the handler). */
export const APPLICATION_LIMITS = {
  legalName: { min: 2, max: 200 },
  registryName: { min: 2, max: 200 },
  registryId: { min: 1, max: 100 },
  registerUrl: { max: 500 },
} as const

export type ApplicationForm = {
  domain: string
  country_code: string
  legal_name: string
  registry_name: string
  registry_id: string
  register_url: string
}

export type ApplicationErrors = Partial<Record<keyof ApplicationForm, string>>

/** A link to a public register entry: https only, and a real URL. */
export function isHttpsUrl(value: string): boolean {
  try {
    const url = new URL(value)
    return url.protocol === 'https:' && !!url.hostname && url.hostname.includes('.')
  } catch {
    return false
  }
}

/** Checks the form like the server does; returns the cleaned body or the errors per field. */
export function validateApplication(
  form: ApplicationForm,
): { ok: true; body: NonprofitApplicationInput } | { ok: false; errors: ApplicationErrors } {
  const errors: ApplicationErrors = {}
  const country = form.country_code.trim().toUpperCase()
  const legal = form.legal_name.trim()
  const registry = form.registry_name.trim()
  const registryId = form.registry_id.trim()
  const link = form.register_url.trim()
  const L = APPLICATION_LIMITS

  if (!form.domain) errors.domain = 'Choose the domain to apply for.'
  if (!/^[A-Z]{2}$/.test(country)) errors.country_code = 'Enter the two-letter country code, for example GB, DE or US.'
  if (legal.length < L.legalName.min || legal.length > L.legalName.max) {
    errors.legal_name = `Enter the legal name (${L.legalName.min} to ${L.legalName.max} characters).`
  }
  if (registry.length < L.registryName.min || registry.length > L.registryName.max) {
    errors.registry_name = `Enter the name of the register (${L.registryName.min} to ${L.registryName.max} characters).`
  }
  if (registryId.length < L.registryId.min || registryId.length > L.registryId.max) {
    errors.registry_id = `Enter the registry or tax number (up to ${L.registryId.max} characters).`
  }
  if (link && (link.length > L.registerUrl.max || !isHttpsUrl(link))) {
    errors.register_url = 'Enter a link that starts with https://, or leave it empty.'
  }
  if (Object.keys(errors).length > 0) return { ok: false, errors }
  return {
    ok: true,
    body: {
      domain: form.domain,
      country_code: country,
      legal_name: legal,
      registry_name: registry,
      registry_id: registryId,
      ...(link ? { register_url: link } : {}),
    },
  }
}

/** A domain an admin types: lower case, no scheme, no path, no `www.`. Empty when it is not a domain. */
export function normalizeDomain(input: string): string {
  let v = input.trim().toLowerCase()
  v = v.replace(/^[a-z]+:\/\//, '').replace(/[/?#].*$/, '').replace(/:\d+$/, '').replace(/^www\./, '').replace(/\.$/, '')
  return /^(?=.{3,253}$)[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(v) ? v : ''
}

/** The pricing FAQ entry. */
export const NONPROFIT_FAQ = {
  question: 'Do you have a nonprofit program?',
  answer:
    `Yes. Registered nonprofits and charities get Pro free for ${GRANT_MONTHS} months, and it renews while they still qualify. ` +
    "Sign up with a work email address on your organization's domain, then claim it in Settings, Billing. " +
    'Free Pro is not meant for government bodies, political parties, groups whose mission is religious worship or for-profit social enterprises. ' +
    'Details: uptrack.app/for/nonprofits.',
} as const

/** Who free Pro is not meant for (D12). The server blocks government domains and checks the rest in review, so the copy says "not meant for". */
export const NOT_ELIGIBLE = [
  'Government bodies',
  'Political parties',
  'Groups whose mission is religious worship',
  'For-profit social enterprises',
] as const

/** Data credits for the two registers the server imports. */
export const REGISTER_CREDITS = {
  uk: 'Contains public sector information licensed under the Open Government Licence v3.0.',
  ukLicenceUrl: 'https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/',
  acnc: 'Contains data from the ACNC Charity Register, licensed under Creative Commons Attribution 3.0 Australia (CC BY 3.0 AU).',
  acncLicenceUrl: 'https://creativecommons.org/licenses/by/3.0/au/',
} as const
